// ===================================================
// File: src/components/common/NotificationPanel.jsx
// Notification Panel Component - Modern Card Design
// ===================================================
import React, { useState } from "react";
import { stripHtml, getAvatarColor } from "../../utils/avatarUtils";
import "./NotificationPanel.scss";

const NotificationPanel = ({
  notifications = [],
  variant = "sidebar", // "sidebar" or "inline"
  isOpen = false,
  onClose,
  onMarkAllRead,
  onMarkAsRead,
  onNotificationClick,
  showHeader = true,
  showTabs = true,
}) => {
  const [activeTab, setActiveTab] = useState("unread");
  const [expandedMessages, setExpandedMessages] = useState({});

  // Filter notifications based on active tab / tabs visibility
  const getFilteredNotifications = () => {
    if (!showTabs) {
      // If filters are hidden, always show all notifications
      return notifications;
    }
    if (activeTab === "unread") {
      return notifications.filter((n) => !n.isRead);
    } else if (activeTab === "read") {
      return notifications.filter((n) => n.isRead);
    }
    return notifications;
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const readCount = notifications.filter((n) => n.isRead).length;
  const totalCount = notifications.length;

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";

    const parts = name
      .trim() // leading/trailing spaces hatao
      .split(/\s+/) // multiple spaces ko ek maan lo
      .filter(Boolean); // safety

    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    return parts[0].substring(0, 2).toUpperCase();
  };

  // Format timestamp to relative time with date and time
  const formatTimestamp = (timestamp) => {
    if (timestamp == null || timestamp === "") return "";
    // If already a formatted string (e.g. "dd/MM/yyyy hh:mm tt" from API), use as-is
    if (typeof timestamp === "string" && !timestamp.match(/^\d{4}-\d{2}-\d{2}/) && !/^\d+$/.test(timestamp)) {
      return timestamp.trim();
    }
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return String(timestamp || "");
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month} ${hours}:${minutes}`;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Get notification type icon
  const getNotificationIcon = (title) => {
    if (
      title.toLowerCase().includes("approved") ||
      title.toLowerCase().includes("success")
    ) {
      return "fas fa-check-circle";
    } else if (title.toLowerCase().includes("review")) {
      return "fas fa-eye";
    } else if (
      title.toLowerCase().includes("change") ||
      title.toLowerCase().includes("request")
    ) {
      return "fas fa-edit";
    }
    return "fas fa-bell";
  };

  // Toggle message expansion
  const toggleMessageExpansion = (notificationId, e) => {
    e.stopPropagation();
    setExpandedMessages((prev) => ({
      ...prev,
      [notificationId]: !prev[notificationId],
    }));
  };

  // Check if message should be truncated (more than 100 characters)
  const shouldTruncate = (message) => {
    if (!message) return false;
    const len = /<[^>]+>/.test(message)
      ? stripHtml(message).length
      : message.length;
    return len > 100;
  };

  // Get truncated message (plain text for HTML content)
  const getTruncatedMessage = (message) => {
    if (!message) return "";
    const plain = /<[^>]+>/.test(message) ? stripHtml(message) : message;
    return plain.length > 100 ? plain.substring(0, 100) + "..." : plain;
  };

  // Check if message contains HTML
  const hasHtml = (msg) => msg && /<[^>]+>/.test(msg);

  // Render message content - supports HTML from API
  const renderMessageContent = (notification) => {
    const msg = notification.message;
    if (!msg) return null;
    const isExpanded = expandedMessages[notification.id];
    const truncated = shouldTruncate(msg);
    const content = isExpanded || !truncated ? msg : getTruncatedMessage(msg);
    if (hasHtml(content)) {
      return (
        <span
          className="notification-panel__card-message-html"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    return content;
  };

  // If sidebar variant and not open, don't render
  if (variant === "sidebar" && !isOpen) {
    return null;
  }

  const panelContent = (
    <>
      {/* Header (optional) */}
      {showHeader && (
        <div className="notification-panel__header">
          <div className="notification-panel__header-left">
            <div className="notification-panel__icon-wrapper">
              <i className="fas fa-bell"></i>
            </div>
            <div>
              <h3 className="notification-panel__title">Notifications</h3>
              {unreadCount > 0 && (
                <span className="notification-panel__count-badge">
                  {unreadCount} new
                </span>
              )}
            </div>
          </div>
          <div className="notification-panel__header-right">
            {onMarkAllRead && unreadCount > 0 && (
              <button
                className="notification-panel__mark-all-btn"
                onClick={onMarkAllRead}
              >
                <i className="fas fa-check-double"></i>
                Mark all read
              </button>
            )}
            {variant === "sidebar" && (
              <button
                className="notification-panel__close-btn"
                onClick={onClose}
                aria-label="Close notifications"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tabs (optional) */}
      {showTabs && (
        <div className="notification-panel__tabs">
          <button
            className={`notification-panel__tab ${activeTab === "unread" ? "active" : ""}`}
            onClick={() => setActiveTab("unread")}
          >
            <span>Unread</span>
            {unreadCount > 0 && (
              <span className="notification-panel__tab-count">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            className={`notification-panel__tab ${activeTab === "read" ? "active" : ""}`}
            onClick={() => setActiveTab("read")}
          >
            <span>Read</span>
            {readCount > 0 && (
              <span className="notification-panel__tab-count">
                {readCount}
              </span>
            )}
          </button>
          <button
            className={`notification-panel__tab ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            <span>All</span>
            {totalCount > 0 && (
              <span className="notification-panel__tab-count">
                {totalCount}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Notification List */}
      <div className="notification-panel__list">
        {filteredNotifications.length === 0 ? (
          <div className="notification-panel__empty">
            <div className="notification-panel__empty-icon">
              <i className="fas fa-inbox"></i>
            </div>
            <p>
              No{" "}
              {activeTab === "unread"
                ? "unread"
                : activeTab === "read"
                  ? "read"
                  : ""}{" "}
              notifications
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-panel__card ${!notification.isRead ? "unread" : ""}`}
              onClick={() =>
                onNotificationClick && onNotificationClick(notification)
              }
            >
              <div className="notification-panel__card-content">
                <div className="notification-panel__card-header">
                  <div className="notification-panel__card-avatar-wrapper">
                    {notification.avatar ? (
                      <img
                        src={notification.avatar}
                        alt={notification.senderName || "User"}
                        className="notification-panel__card-avatar-img"
                      />
                    ) : (
                      <div
                        className="notification-panel__card-avatar"
                        title={notification.senderName || ""}
                        style={{
                          backgroundColor: getAvatarColor(
                            notification.senderName || "?"
                          ),
                        }}
                      >
                        {getInitials(notification.senderName)}
                      </div>
                    )}
                  </div>
                  <div className="notification-panel__card-header-content">
                    <div className="notification-panel__card-header-text">
                      <h4 className="notification-panel__card-title">
                        {notification.title}
                      </h4>
                      <div className="notification-panel__card-meta">
                        {notification.senderName && (
                          <span className="notification-panel__card-sender">
                            {notification.senderName}
                          </span>
                        )}
                        <span className="notification-panel__card-time">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`notification-panel__card-message-wrapper ${
                        shouldTruncate(notification.message)
                          ? expandedMessages[notification.id]
                            ? "notification-panel__card-message-wrapper--expanded"
                            : "notification-panel__card-message-wrapper--collapsed"
                          : ""
                      }`}
                    >
                      <p className="notification-panel__card-message">
                        {renderMessageContent(notification)}
                      </p>
                      {shouldTruncate(notification.message) && (
                        <button
                          className="notification-panel__card-message-toggle"
                          onClick={(e) =>
                            toggleMessageExpansion(notification.id, e)
                          }
                        >
                          {expandedMessages[notification.id]
                            ? "Show less"
                            : "Show more"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="notification-panel__card-header-right">
                    {!notification.isRead && onMarkAsRead && (
                      <button
                        className="notification-panel__card-mark-read"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(notification.id);
                        }}
                      >
                        <i className="fas fa-check"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  // Sidebar variant
  if (variant === "sidebar") {
    return (
      <>
        <div className="notification-panel-overlay" onClick={onClose}></div>
        <div className="notification-panel notification-panel--sidebar">
          {panelContent}
        </div>
      </>
    );
  }

  // Inline variant
  return (
    <div className="notification-panel notification-panel--inline">
      {panelContent}
    </div>
  );
};

export default NotificationPanel;
